import { Box, Grid, Card, CardContent, Typography, Skeleton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useSubscriptionStats } from '../../hooks/subscriptions';
import { formatPrice } from '../../utils/subscriptions';
import { RevenueChart } from '../../components/subscriptions/RevenueChart';
import { PlanDistributionChart } from '../../components/subscriptions/PlanDistributionChart';
import { ModuleAdoptionChart } from '../../components/subscriptions/ModuleAdoptionChart';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
  loading?: boolean;
}

const MetricCard = ({ title, value, icon, color, trend, loading }: MetricCardProps) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <Skeleton width="60%" height={40} />
            ) : (
              <Typography variant="h4" fontWeight={700} color={color} sx={{ mb: 1 }}>
                {value}
              </Typography>
            )}
            {trend && !loading && (
              <Typography variant="caption" color="success.main">
                {trend}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: color + '15',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const SubscriptionDashboard = () => {
  const { data: stats, isLoading } = useSubscriptionStats();

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Subscription Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Overview of subscription metrics and revenue
        </Typography>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Organizations"
            value={stats?.totalOrganizations || 0}
            icon={<BusinessIcon />}
            color="#6366F1"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Subscriptions"
            value={stats?.activeSubscriptions || 0}
            icon={<PeopleIcon />}
            color="#10B981"
            trend={stats ? `${((stats.activeSubscriptions / stats.totalOrganizations) * 100).toFixed(1)}% of total` : undefined}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Monthly Recurring Revenue"
            value={stats ? formatPrice(stats.monthlyRecurringRevenue) : '$0.00'}
            icon={<AttachMoneyIcon />}
            color="#F59E0B"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Annual Recurring Revenue"
            value={stats ? formatPrice(stats.annualRecurringRevenue) : '$0.00'}
            icon={<TrendingUpIcon />}
            color="#8B5CF6"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Additional Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Trial Subscriptions"
            value={stats?.trialingSubscriptions || 0}
            icon={<PeopleIcon />}
            color="#06B6D4"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Average Revenue/Org"
            value={stats ? formatPrice(stats.averageRevenuePerOrg) : '$0.00'}
            icon={<AttachMoneyIcon />}
            color="#3B82F6"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Trial Conversion Rate"
            value={stats ? `${(stats.trialConversionRate * 100).toFixed(1)}%` : '0%'}
            icon={<TrendingUpIcon />}
            color="#10B981"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Churn Rate"
            value={stats ? `${(stats.churnRate * 100).toFixed(1)}%` : '0%'}
            icon={<TrendingUpIcon />}
            color={stats && stats.churnRate > 0.05 ? '#EF4444' : '#10B981'}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} mb={4}>
        {/* Revenue Chart */}
        <Grid item xs={12}>
          <RevenueChart />
        </Grid>

        {/* Plan Distribution Chart */}
        <Grid item xs={12} md={6}>
          <PlanDistributionChart />
        </Grid>

        {/* Module Adoption Chart */}
        <Grid item xs={12} md={6}>
          <ModuleAdoptionChart />
        </Grid>
      </Grid>
    </Box>
  );
};
